import { Box, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { ApiError } from './api/client';
import { AcceptInvitationPage } from './auth/AcceptInvitationPage';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { LoginPage } from './auth/LoginPage';
import { InstallPage } from './auth/InstallPage';
import { RegisterPage } from './auth/RegisterPage';
import { AppShell } from './layout/AppShell';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { featureRoutes } from './features';
import { DashboardPage } from './features/shop/dashboard/DashboardPage';
import { ShopProvider } from './shop/ShopProvider';

/** The point of sale is full screen (outside the app shell) and loads on first use. */
const PosApp = lazy(() => import('./features/pos/PosApp').then((m) => ({ default: m.PosApp })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
    },
  },
});

function Splash() {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress size={28} />
    </Box>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <Splash />;
  if (status === 'signedOut')
    return (
      <Navigate
        to={location.pathname.startsWith('/pos') ? '/pos/login' : '/login'}
        replace
        state={{ from: location.pathname }}
      />
    );
  return children;
}

function GuestOnly({ children, home = '/' }: { children: ReactNode; home?: string }) {
  const { status } = useAuth();
  if (status === 'loading') return <Splash />;
  if (status === 'signedIn') return <Navigate to={home} replace />;
  return children;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ShopProvider>
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/login"
                  element={
                    <GuestOnly>
                      <LoginPage />
                    </GuestOnly>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <GuestOnly>
                      <RegisterPage />
                    </GuestOnly>
                  }
                />
                <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                <Route path="/install" element={<InstallPage />} />
                <Route
                  path="/pos/login"
                  element={
                    <GuestOnly home="/pos">
                      <LoginPage target="pos" />
                    </GuestOnly>
                  }
                />
                <Route
                  path="/pos/*"
                  element={
                    <RequireAuth>
                      <Suspense fallback={<Splash />}>
                        <PosApp />
                      </Suspense>
                    </RequireAuth>
                  }
                />
                <Route
                  element={
                    <RequireAuth>
                      <AppShell />
                    </RequireAuth>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  {featureRoutes.map((r) => (
                    <Route key={r.path} path={r.path} element={r.element} />
                  ))}
                  <Route path="*" element={<ComingSoonPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </SnackbarProvider>
        </ShopProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
